/**
 * @file App.tsx
 * @description Main component for the FpS Content Generator frontend application.
 * Provides the form for user input and displays the generated SEO content.
 * Handles the state management and API calls for content generation.
 */

import React, { useRef } from "react";
import { HashRouter as Router, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { HostElementsProvider } from "../context/HostElementsContext";
import { NavbarProvider } from "../context/NavbarContext";
import { useUserDetails } from "../context/UserDetailsContext";

import ScrollToTop from "./common/ScrollToTop";
import NavbarPortal from "../components/layout/NavbarPortal";
import PrivacyConsentModal from "../components/common/PrivacyConsentModal";

import GenerateContentLanding from "../pages/Content/GenerateContentLanding";
import GenerateContent from "../pages/Content/GenerateContent";
import Content from "../pages/Content/Content";
import ContentArchive from "../pages/Content/ContentArchive";
import GeneratePlan from "../pages/Plan/GeneratePlan";
import Plan from "../pages/Plan/Plan";
import PlanArchive from "../pages/Plan/PlanArchive";
import SignIn from "../pages/Auth/SignIn";
import Account from "../pages/Account/Account";
import Checkout from "../pages/Checkout/Checkout";
import Faq from "../pages/Support/Faq";

const App: React.FC = () => {
  const { showConsentModal, setShowConsentModal, updateUserConsent } =
    useUserDetails();
  const consentDialogRef = useRef<HTMLDialogElement>(null);

  const handleConsent = async () => {
    await updateUserConsent();
    setShowConsentModal(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <HostElementsProvider>
        <NavbarProvider>
          <NavbarPortal />
          <main className="w-full max-w-screen-xl py-4 mx-auto group sm:py-16 !mt-2 !mb-32">
            <ToastContainer hideProgressBar={true} />
            <Routes>
              <Route path="/" element={<GenerateContentLanding />} />
              <Route path="/genera-contenuto" element={<GenerateContent />} />
              <Route path="/contenuto/:id" element={<Content />} />
              <Route path="/archivio-contenuti" element={<ContentArchive />} />
              <Route path="/genera-piano" element={<GeneratePlan />} />
              <Route path="/genera-piano/:id" element={<GeneratePlan />} />
              <Route path="/piano/:id" element={<Plan />} />
              <Route path="/archivio-piani" element={<PlanArchive />} />
              <Route path="/accedi" element={<SignIn />} />
              <Route
                path="/registrati"
                element={<SignIn showSignUp={true} />}
              />
              <Route path="/account" element={<Account />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/faq" element={<Faq />} />
            </Routes>
          </main>
          {showConsentModal && (
            <PrivacyConsentModal
              onConsent={handleConsent}
              dialogRef={consentDialogRef}
            />
          )}
        </NavbarProvider>
      </HostElementsProvider>
    </Router>
  );
};

export default App;
